import { Component, Input } from '@angular/core';
import { ServiceProvider } from '@sabstravtech/obtservices/base';
import {
  BasketItem,
  WithSubscriptionComponent,
  CarHireType,
  CarHireDetailRequestResponse,
  CarHireAdditionalInfoItem,
  ModalOpenerService,
  EnterpriseSearchService,
  CarHireVehicleAvailibility,
  ServiceType,
  CarHireIdentifiers
} from '@sabstravtech/obtservices/angular';
import { BehaviorSubject } from 'rxjs';

import { LightningModalTypes } from '../../core/classes/modal-types.enum';

@Component({
  selector: 'app-car-details',
  template: '',
  standalone: false
})
export class CarDetailsComponent extends WithSubscriptionComponent {
  @Input() car: BasketItem;
  public carDetailLoadingStates: BehaviorSubject<boolean>[] = [];
  public carTypeCodes: [string, string, CarHireType][];
  public carHireAvailabilityDetailsObject: Record<string, CarHireDetailRequestResponse> = {};

  constructor(
    protected modalService: ModalOpenerService,
    protected searchService: EnterpriseSearchService
  ) {
    super();
    this.carTypeCodes = this.searchService.searches[ServiceType.Car].carTypeCodes;
  }

  /**
    @desc - for a given result retrieve extra details (e.g. availability detail)
    @param result of type CarHireVehicleAvailibility
  **/
  getCarHireAvailabilityDetail(result: CarHireVehicleAvailibility, resultIndex?: number): void {
    const loadingState = resultIndex
      ? this.carDetailLoadingStates[resultIndex]
      : new BehaviorSubject(false);
    const { __typename, ...identifiers } = result.identifiers;

    if (result.source === ServiceProvider.TravelportCarHire) {
      const detailResult: CarHireDetailRequestResponse = {
        additionalInfo: result.displayRecord.additionalInfo,
        address: result.displayRecord.pickupLocation,
        identifiers: result.identifiers,
        vendor: result.displayRecord.vendor
      };

      detailResult.additionalInfo?.sort((a, b) =>
        a.name > b.name ? 1 : a.name === b.name ? 0 : -1
      );
      // move the number to the beginning of the array
      const phone = detailResult.additionalInfo?.find(
        (item: CarHireAdditionalInfoItem) => item.name === 'Contact Numbers'
      );
      detailResult.additionalInfo?.sort((a, b) => (a === phone ? -1 : b == phone ? 1 : 0));

      // store result in an object so we don't have to load the same data again if it's been previously loaded
      this.updateCarHireAvailabilityDetailsObject(result, {
        key: this.createKeyFromIdentifiers(identifiers),
        value: detailResult
      });
    } else {
      const vendor = identifiers.vendor === 'Enterprise' ? 'ET' : identifiers.vendor;
      this.subscribe(
        this.searchService.searches[ServiceType.Car].getCarHireAvailabilityDetail(
          {
            dropOffLocation: identifiers.dropOffLocation,
            dropOffDateTime: identifiers.dropOffDateTime,
            pickUpDateTime: identifiers.pickUpDateTime,
            pickUpExtendedLocationCode: identifiers.pickUpExtendedLocationCode,
            pickUpLocationCode: identifiers.pickUpLocationCode,
            pickUpLocationName: identifiers.pickUpLocationName,
            locator: '',
            rph: '',
            vehType: identifiers.vehType,
            vendor: vendor,
            service: result.source,
            rateCode: result.displayRecord.rateCode
          },
          loadingState
        ),
        (fetchedResult: CarHireDetailRequestResponse) => {
          fetchedResult.additionalInfo.sort((a, b) =>
            a.name > b.name ? 1 : a.name === b.name ? 0 : -1
          );
          // move the number to the beginning of the array
          const phone = fetchedResult.additionalInfo.find(
            (item: CarHireAdditionalInfoItem) => item.name === 'Contact Numbers'
          );
          fetchedResult.additionalInfo.sort((a, b) => (a === phone ? -1 : b == phone ? 1 : 0));

          // store result in an object so we don't have to load the same data again if it's been previously loaded
          this.updateCarHireAvailabilityDetailsObject(result, {
            key: this.createKeyFromIdentifiers(identifiers),
            value: fetchedResult
          });
        }
      );
    }
  }

  /**
    @desc - update the carHireAvailabilityDetailsObject with the provided key and value
    @param data object containing key value pair
  **/
  updateCarHireAvailabilityDetailsObject(
    result: CarHireVehicleAvailibility,
    data: {
      key: string;
      value: CarHireDetailRequestResponse;
    }
  ): void {
    this.carHireAvailabilityDetailsObject[data.key] = data.value;
    this.modalService.open(
      LightningModalTypes.ModalCarDetailsComponent,
      { centered: true },
      {
        result: result,
        carHireAvailabilityDetailsObject: this.carHireAvailabilityDetailsObject,
        carTypeCodes: this.carTypeCodes
      }
    );
  }

  /**
      @desc - for a provided identifiers param return a key string by combining the 'vendor' and 'vehType' properties
      @return string containing key e.g. 'vendor_vehType'. This will be used in carHireAvailabilityDetailsObject
    **/
  createKeyFromIdentifiers(identifiers: CarHireIdentifiers): string {
    return `${identifiers.vendor}_${identifiers.vehType}`;
  }
}
